import React from 'react';
import { Link } from 'react-router-dom';
import { AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { useNoizaMetrics } from '../../lib/noiza-metrics-hooks';
import { formatCurrency } from '../../lib/utils';
import { TrendingUpIcon, ShoppingCartIcon, ReceiptIcon, StoreIcon, UsersIcon, UserCheckIcon, ArrowLeftIcon, ActivityIcon, RefreshCwIcon, Loader2Icon } from 'lucide-react';
// ─── Helpers ───────────────────────────────────────────────
function formatCompactCurrency(value: number): string {
  if (value >= 1000000) return `₡${(value / 1000000).toFixed(1)}M`;
  if (value >= 1000) return `₡${(value / 1000).toFixed(0)}k`;
  return formatCurrency(value);
}
function formatDayLabel(dateStr: string): string {
  if (!dateStr) return '';
  try {
    const [y, m, d] = dateStr.split('-').map(Number);
    const months = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];
    return `${d} ${months[m - 1]}`;
  } catch {
    return dateStr;
  }
}
function normalizeChannel(ch: string): string {
  const lower = (ch || '').toLowerCase();
  if (lower.includes('qr') || lower.includes('self')) return 'QR';
  if (lower.includes('waiter') || lower.includes('mesero')) return 'Mesero';
  if (lower.includes('web') || lower.includes('online')) return 'Web';
  if (lower.includes('caja') || lower.includes('cashier')) return 'Caja';
  return ch || 'Otro';
}
const CHANNEL_COLORS: Record<string, string> = {
  QR: '#10b981',
  Mesero: '#6366f1',
  Web: '#f59e0b',
  Caja: '#8b5cf6',
  Otro: '#6b7280'
};
// ─── Dark Tooltip ──────────────────────────────────────────
function DarkTooltip({
  active,
  payload,
  label,
  isCurrency
}: any) {
  if (!active || !payload?.length) return null;
  return <div className="bg-white/10 backdrop-blur-xl border border-white/10 text-white px-4 py-3 rounded-xl shadow-2xl text-sm" data-id="element-1702">
      <p className="font-medium text-white/60 mb-1.5 text-xs uppercase tracking-wider" data-id="element-1703">
        {label}
      </p>
      {payload.map((entry: any, i: number) => <p key={i} className="flex items-center gap-2" data-id="element-1704">
          <span className="w-2 h-2 rounded-full" style={{
        backgroundColor: entry.color
      }} data-id="element-1705" />
          <span className="text-white/50" data-id="element-1706">{entry.name}:</span>
          <span className="font-semibold text-white" data-id="element-1707">
            {isCurrency ? formatCurrency(entry.value) : entry.value.toLocaleString()}
          </span>
        </p>)}
    </div>;
}
// ─── KPI Card ──────────────────────────────────────────────
interface KpiCardProps {
  label: string;
  value: string;
  subtitle?: string;
  icon: React.ReactNode;
  accentColor: string;
}
function KpiCard({
  label,
  value,
  subtitle,
  icon,
  accentColor
}: KpiCardProps) {
  return <div className="relative overflow-hidden rounded-2xl bg-[#12121a] border border-white/[0.06] p-5 group" data-id="element-1708">
      <div className={`absolute top-0 right-0 w-32 h-32 rounded-full blur-3xl opacity-[0.07] -translate-y-8 translate-x-8`} style={{
      backgroundColor: accentColor
    }} data-id="element-1709" />
      <div className="relative" data-id="element-1710">
        <div className="flex items-center justify-between mb-4" data-id="element-1711">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{
          backgroundColor: `${accentColor}15`
        }} data-id="element-1712">
            {icon}
          </div>
        </div>
        <p className="text-[13px] font-medium text-white/40 uppercase tracking-wider" data-id="element-1713">
          {label}
        </p>
        <p className="text-3xl font-bold text-white mt-1 tracking-tight" data-id="element-1714">
          {value}
        </p>
        {subtitle && <p className="text-xs text-white/30 mt-1" data-id="element-1715">{subtitle}</p>}
      </div>
    </div>;
}
// ─── Main Page ─────────────────────────────────────────────
export function NoizaMetricsPage() {
  const {
    data,
    loading,
    error,
    refetch
  } = useNoizaMetrics();
  const dailyChartData = data.gmvDaily.map(d => ({
    ...d,
    label: formatDayLabel(d.day)
  }));
  const channelChartData = data.channelDistribution.map(d => ({
    name: normalizeChannel(d.channel),
    value: d.orders,
    pct: d.pct,
    color: CHANNEL_COLORS[normalizeChannel(d.channel)] || '#6b7280'
  })).filter(d => d.value > 0);
  const totalChannelOrders = channelChartData.reduce((sum, d) => sum + d.value, 0);
  return <div className="min-h-screen bg-[#0a0a0f] text-white" data-id="element-1716">
      {/* ─── Header ─────────────────────────────────────── */}
      <header className="sticky top-0 z-50 bg-[#0a0a0f]/80 backdrop-blur-xl border-b border-white/[0.06]" data-id="element-1717">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between" data-id="element-1718">
          <div className="flex items-center gap-4" data-id="element-1719">
            <Link to="/" className="w-9 h-9 rounded-xl bg-white/[0.06] flex items-center justify-center hover:bg-white/[0.1] transition-colors" data-id="element-1720">
              <ArrowLeftIcon className="w-4 h-4 text-white/60" data-id="element-1721" />
            </Link>
            <div data-id="element-1722">
              <div className="flex items-center gap-2.5" data-id="element-1723">
                <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center shadow-lg shadow-amber-500/20" data-id="element-1724">
                  <ActivityIcon className="w-4 h-4 text-white" data-id="element-1725" />
                </div>
                <h1 className="text-lg font-bold tracking-tight" data-id="element-1726">NOIZA</h1>
                <span className="text-xs font-medium text-white/30 bg-white/[0.06] px-2 py-0.5 rounded-md" data-id="element-1727">
                  Platform Metrics
                </span>
              </div>
            </div>
          </div>
          <button onClick={refetch} disabled={loading} className="flex items-center gap-2 px-3 py-2 rounded-xl bg-white/[0.06] hover:bg-white/[0.1] transition-colors text-sm text-white/60 hover:text-white/80 disabled:opacity-50" data-id="element-1728">
            {loading ? <Loader2Icon className="w-4 h-4 animate-spin" data-id="element-1729" /> : <RefreshCwIcon className="w-4 h-4" data-id="element-1730" />}
            <span className="hidden sm:inline" data-id="element-1731">Actualizar</span>
          </button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8 space-y-8" data-id="element-1732">
        {/* ─── Error State ────────────────────────────── */}
        {error && <div className="rounded-2xl bg-red-500/10 border border-red-500/20 p-4 text-sm text-red-400" data-id="element-1733">
            {error}
          </div>}

        {/* ─── Loading State ──────────────────────────── */}
        {loading ? <div className="space-y-8" data-id="element-1734">
            <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4" data-id="element-1735">
              {Array.from({
            length: 6
          }).map((_, i) => <div key={i} className="h-32 rounded-2xl bg-white/[0.03] animate-pulse" data-id="element-1736" />)}
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4" data-id="element-1737">
              <div className="lg:col-span-2 h-80 rounded-2xl bg-white/[0.03] animate-pulse" data-id="element-1738" />
              <div className="h-80 rounded-2xl bg-white/[0.03] animate-pulse" data-id="element-1739" />
            </div>
            <div className="h-64 rounded-2xl bg-white/[0.03] animate-pulse" data-id="element-1740" />
          </div> : <>
            {/* ─── KPI Cards ──────────────────────────── */}
            <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4" data-id="element-1741">
              <KpiCard label="GMV Total" value={formatCompactCurrency(data.gmv.total_gmv)} subtitle="Gross Merchandise Value" icon={<TrendingUpIcon className="w-5 h-5 text-emerald-400" data-id="element-1743" />} accentColor="#10b981" data-id="element-1742" />
              <KpiCard label="Órdenes" value={data.gmv.total_orders.toLocaleString()} subtitle="Total procesadas" icon={<ShoppingCartIcon className="w-5 h-5 text-blue-400" data-id="element-1745" />} accentColor="#3b82f6" data-id="element-1744" />
              <KpiCard label="Ticket Promedio" value={formatCurrency(data.gmv.avg_ticket)} subtitle="Por orden" icon={<ReceiptIcon className="w-5 h-5 text-amber-400" data-id="element-1747" />} accentColor="#f59e0b" data-id="element-1746" />
              <KpiCard label="Restaurantes" value={String(data.activeRestaurants)} subtitle="Activos en plataforma" icon={<StoreIcon className="w-5 h-5 text-purple-400" data-id="element-1749" />} accentColor="#8b5cf6" data-id="element-1748" />
              <KpiCard label="Clientes Únicos" value={data.uniqueCustomers.toLocaleString()} subtitle="Compradores distintos" icon={<UsersIcon className="w-5 h-5 text-cyan-400" data-id="element-1751" />} accentColor="#06b6d4" data-id="element-1750" />
              <KpiCard label="Recurrentes" value={data.repeatCustomers.repeat_count.toLocaleString()} subtitle={data.repeatCustomers.repeat_pct > 0 ? `${Math.round(data.repeatCustomers.repeat_pct)}% del total` : 'Clientes que regresan'} icon={<UserCheckIcon className="w-5 h-5 text-pink-400" data-id="element-1753" />} accentColor="#ec4899" data-id="element-1752" />
            </div>

            {/* ─── Charts Row ─────────────────────────── */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4" data-id="element-1754">
              {/* GMV Daily Chart */}
              <div className="lg:col-span-2 rounded-2xl bg-[#12121a] border border-white/[0.06] p-5" data-id="element-1755">
                <div className="flex items-center justify-between mb-6" data-id="element-1756">
                  <div data-id="element-1757">
                    <h2 className="text-sm font-semibold text-white/80" data-id="element-1758">
                      GMV Diario
                    </h2>
                    <p className="text-xs text-white/30 mt-0.5" data-id="element-1759">
                      Ingresos brutos por día
                    </p>
                  </div>
                  <div className="flex items-center gap-4 text-xs text-white/30" data-id="element-1760">
                    <span className="flex items-center gap-1.5" data-id="element-1761">
                      <span className="w-2 h-2 rounded-full bg-emerald-400" data-id="element-1762" />
                      GMV
                    </span>
                  </div>
                </div>
                {dailyChartData.length === 0 ? <div className="h-64 flex items-center justify-center text-white/20 text-sm" data-id="element-1763">
                    Sin datos disponibles
                  </div> : <div className="h-64" data-id="element-1764">
                    <ResponsiveContainer width="100%" height="100%" data-id="element-1765">
                      <AreaChart data={dailyChartData} margin={{
                  top: 4,
                  right: 4,
                  left: 0,
                  bottom: 0
                }} data-id="element-1766">
                        <defs data-id="element-1767">
                          <linearGradient id="noizaGmvGrad" x1="0" y1="0" x2="0" y2="1" data-id="element-1768">
                            <stop offset="0%" stopColor="#10b981" stopOpacity={0.3} data-id="element-1769" />
                            <stop offset="100%" stopColor="#10b981" stopOpacity={0} data-id="element-1770" />
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.04)" vertical={false} data-id="element-1771" />
                        <XAxis dataKey="label" tick={{
                    fontSize: 11,
                    fill: 'rgba(255,255,255,0.25)'
                  }} tickLine={false} axisLine={{
                    stroke: 'rgba(255,255,255,0.06)'
                  }} interval={dailyChartData.length > 15 ? Math.floor(dailyChartData.length / 8) : 0} data-id="element-1772" />
                        <YAxis tick={{
                    fontSize: 11,
                    fill: 'rgba(255,255,255,0.25)'
                  }} tickLine={false} axisLine={false} tickFormatter={v => formatCompactCurrency(v)} data-id="element-1773" />
                        <Tooltip content={<DarkTooltip isCurrency data-id="element-1775" />} data-id="element-1774" />
                        <Area type="monotone" dataKey="gmv" name="GMV" stroke="#10b981" strokeWidth={2} fill="url(#noizaGmvGrad)" dot={false} activeDot={{
                    r: 4,
                    fill: '#10b981',
                    stroke: '#0a0a0f',
                    strokeWidth: 2
                  }} data-id="element-1776" />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>}
              </div>

              {/* Channel Distribution */}
              <div className="rounded-2xl bg-[#12121a] border border-white/[0.06] p-5" data-id="element-1777">
                <div className="mb-4" data-id="element-1778">
                  <h2 className="text-sm font-semibold text-white/80" data-id="element-1779">
                    Canales
                  </h2>
                  <p className="text-xs text-white/30 mt-0.5" data-id="element-1780">
                    Distribución de pedidos
                  </p>
                </div>
                {channelChartData.length === 0 ? <div className="h-48 flex items-center justify-center text-white/20 text-sm" data-id="element-1781">
                    Sin datos
                  </div> : <>
                    <div className="h-48" data-id="element-1782">
                      <ResponsiveContainer width="100%" height="100%" data-id="element-1783">
                        <PieChart data-id="element-1784">
                          <Pie data={channelChartData} cx="50%" cy="50%" innerRadius={50} outerRadius={75} paddingAngle={3} dataKey="value" strokeWidth={0} data-id="element-1785">
                            {channelChartData.map((entry, i) => <Cell key={i} fill={entry.color} data-id="element-1786" />)}
                          </Pie>
                          <Tooltip content={({
                      active,
                      payload
                    }) => {
                      if (!active || !payload?.length) return null;
                      const d = payload[0].payload;
                      const pct = totalChannelOrders > 0 ? Math.round(d.value / totalChannelOrders * 100) : 0;
                      return <div className="bg-white/10 backdrop-blur-xl border border-white/10 text-white px-3 py-2 rounded-xl shadow-2xl text-sm" data-id="element-1788">
                                  <p className="font-medium" data-id="element-1789">{d.name}</p>
                                  <p className="text-white/50" data-id="element-1790">
                                    {d.value.toLocaleString()} pedidos ({pct}%)
                                  </p>
                                </div>;
                    }} data-id="element-1787" />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                    <div className="space-y-2 mt-2" data-id="element-1791">
                      {channelChartData.map(entry => {
                  const pct = totalChannelOrders > 0 ? Math.round(entry.value / totalChannelOrders * 100) : 0;
                  return <div key={entry.name} className="flex items-center justify-between" data-id="element-1792">
                            <div className="flex items-center gap-2" data-id="element-1793">
                              <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{
                        backgroundColor: entry.color
                      }} data-id="element-1794" />
                              <span className="text-sm text-white/60" data-id="element-1795">
                                {entry.name}
                              </span>
                            </div>
                            <div className="flex items-center gap-3" data-id="element-1796">
                              <span className="text-sm font-semibold text-white/90" data-id="element-1797">
                                {entry.value.toLocaleString()}
                              </span>
                              <span className="text-xs text-white/30 w-10 text-right" data-id="element-1798">
                                {pct}%
                              </span>
                            </div>
                          </div>;
                })}
                    </div>
                  </>}
              </div>
            </div>

            {/* ─── Orders per Day Chart ───────────────── */}
            <div className="rounded-2xl bg-[#12121a] border border-white/[0.06] p-5" data-id="element-1799">
              <div className="flex items-center justify-between mb-6" data-id="element-1800">
                <div data-id="element-1801">
                  <h2 className="text-sm font-semibold text-white/80" data-id="element-1802">
                    Órdenes Diarias
                  </h2>
                  <p className="text-xs text-white/30 mt-0.5" data-id="element-1803">
                    Volumen de pedidos por día
                  </p>
                </div>
              </div>
              {dailyChartData.length === 0 ? <div className="h-56 flex items-center justify-center text-white/20 text-sm" data-id="element-1804">
                  Sin datos disponibles
                </div> : <div className="h-56" data-id="element-1805">
                  <ResponsiveContainer width="100%" height="100%" data-id="element-1806">
                    <BarChart data={dailyChartData} margin={{
                top: 4,
                right: 4,
                left: 0,
                bottom: 0
              }} data-id="element-1807">
                      <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.04)" vertical={false} data-id="element-1808" />
                      <XAxis dataKey="label" tick={{
                  fontSize: 11,
                  fill: 'rgba(255,255,255,0.25)'
                }} tickLine={false} axisLine={{
                  stroke: 'rgba(255,255,255,0.06)'
                }} interval={dailyChartData.length > 15 ? Math.floor(dailyChartData.length / 8) : 0} data-id="element-1809" />
                      <YAxis tick={{
                  fontSize: 11,
                  fill: 'rgba(255,255,255,0.25)'
                }} tickLine={false} axisLine={false} data-id="element-1810" />
                      <Tooltip content={<DarkTooltip isCurrency={false} data-id="element-1812" />} data-id="element-1811" />
                      <Bar dataKey="orders" name="Órdenes" fill="#6366f1" radius={[3, 3, 0, 0]} maxBarSize={32} opacity={0.8} data-id="element-1813" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>}
            </div>

            {/* ─── Top Restaurants Table ───────────────── */}
            <div className="rounded-2xl bg-[#12121a] border border-white/[0.06] overflow-hidden" data-id="element-1814">
              <div className="p-5 border-b border-white/[0.06]" data-id="element-1815">
                <h2 className="text-sm font-semibold text-white/80" data-id="element-1816">
                  Top Restaurantes por GMV
                </h2>
                <p className="text-xs text-white/30 mt-0.5" data-id="element-1817">
                  Rendimiento por establecimiento
                </p>
              </div>
              {data.gmvByRestaurant.length === 0 ? <div className="p-8 text-center text-white/20 text-sm" data-id="element-1818">
                  Sin datos de restaurantes
                </div> : <div className="overflow-x-auto" data-id="element-1819">
                  <table className="w-full text-sm" data-id="element-1820">
                    <thead data-id="element-1821">
                      <tr className="text-xs text-white/30 uppercase tracking-wider" data-id="element-1822">
                        <th className="text-left px-5 py-3 font-medium" data-id="element-1823">#</th>
                        <th className="text-left px-5 py-3 font-medium" data-id="element-1824">
                          Restaurante
                        </th>
                        <th className="text-right px-5 py-3 font-medium" data-id="element-1825">
                          GMV
                        </th>
                        <th className="text-right px-5 py-3 font-medium" data-id="element-1826">
                          Órdenes
                        </th>
                        <th className="text-right px-5 py-3 font-medium" data-id="element-1827">
                          Ticket Prom.
                        </th>
                        <th className="text-right px-5 py-3 font-medium" data-id="element-1828">
                          % del Total
                        </th>
                      </tr>
                    </thead>
                    <tbody data-id="element-1829">
                      {data.gmvByRestaurant.map((r, i) => {
                  const gmvPct = data.gmv.total_gmv > 0 ? (r.gmv / data.gmv.total_gmv * 100).toFixed(1) : '0';
                  return <tr key={r.restaurant_id || i} className="border-t border-white/[0.04] hover:bg-white/[0.02] transition-colors" data-id="element-1830">
                            <td className="px-5 py-3.5" data-id="element-1831">
                              <span className={`w-6 h-6 rounded-lg text-xs font-bold flex items-center justify-center ${i === 0 ? 'bg-amber-500/20 text-amber-400' : i === 1 ? 'bg-white/[0.08] text-white/50' : i === 2 ? 'bg-orange-500/15 text-orange-400/80' : 'bg-transparent text-white/20'}`} data-id="element-1832">
                                {i + 1}
                              </span>
                            </td>
                            <td className="px-5 py-3.5" data-id="element-1833">
                              <div data-id="element-1834">
                                <p className="font-medium text-white/90" data-id="element-1835">
                                  {r.restaurant_name}
                                </p>
                                {r.slug && <p className="text-xs text-white/25 mt-0.5" data-id="element-1836">
                                    {r.slug}
                                  </p>}
                              </div>
                            </td>
                            <td className="px-5 py-3.5 text-right font-semibold text-emerald-400" data-id="element-1837">
                              {formatCurrency(r.gmv)}
                            </td>
                            <td className="px-5 py-3.5 text-right text-white/60" data-id="element-1838">
                              {r.orders.toLocaleString()}
                            </td>
                            <td className="px-5 py-3.5 text-right text-white/60" data-id="element-1839">
                              {formatCurrency(r.avg_ticket)}
                            </td>
                            <td className="px-5 py-3.5 text-right" data-id="element-1840">
                              <div className="flex items-center justify-end gap-2" data-id="element-1841">
                                <div className="w-16 h-1.5 rounded-full bg-white/[0.06] overflow-hidden" data-id="element-1842">
                                  <div className="h-full rounded-full bg-emerald-500/60" style={{
                            width: `${Math.min(parseFloat(gmvPct), 100)}%`
                          }} data-id="element-1843" />
                                </div>
                                <span className="text-xs text-white/40 w-10 text-right" data-id="element-1844">
                                  {gmvPct}%
                                </span>
                              </div>
                            </td>
                          </tr>;
                })}
                    </tbody>
                  </table>
                </div>}
            </div>

            {/* ─── Footer ─────────────────────────────── */}
            <div className="text-center py-4" data-id="element-1845">
              <p className="text-xs text-white/15" data-id="element-1846">
                NOIZA Platform Analytics · Datos actualizados cada minuto
              </p>
            </div>
          </>}
      </main>
    </div>;
}